import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { DisplayItem } from '@/types/itemDisplay';

/**
 * Contexte de comparaison d'objets (cf wiki §24.12 — loot Diablo-like).
 *
 * UX : le joueur épingle un objet de RÉFÉRENCE (« objet à remplacer ») via le
 * bouton ⇄ d'une `<ItemDisplay comparable>`. Tant qu'un objet est épinglé, le
 * tooltip de TOUT autre objet (`<ItemTooltipContent>`) affiche les deltas
 * stat-à-stat par rapport à la référence. Cliquer à nouveau dépingle.
 *
 * État volontairement global (un seul épinglage actif à la fois) pour que la
 * comparaison fonctionne d'un objet à l'autre à travers toute une vue, voire
 * entre vues (ex : inventaire vs marché). Pas de persistance.
 */
export const useItemComparisonStore = defineStore('itemComparison', () => {
  const pinned = ref<DisplayItem | null>(null);

  const hasPinned = computed(() => pinned.value !== null);

  function isPinned(id: string | number): boolean {
    return pinned.value?.id === id;
  }

  /** Épingle (ou dépingle si déjà épinglé) l'objet comme référence. */
  function toggle(item: DisplayItem): void {
    pinned.value = pinned.value?.id === item.id ? null : item;
  }

  function pin(item: DisplayItem): void {
    pinned.value = item;
  }

  function clear(): void {
    pinned.value = null;
  }

  return { pinned, hasPinned, isPinned, toggle, pin, clear };
});
